import { type DicomJson, tagString } from '../../dicomweb';
import { formatDicomDate } from '../../overlay';
import type { ViewerShell } from '../../shell';
import { fetchInstanceMetadata } from '../../ui/dialogs';
import { frameAt } from './context';

/**
 * Questions about the acquisition ("¿con contraste?", "¿grosor de corte?", "¿kVp?") answered from the
 * DICOM header of the image on screen, without sending anything anywhere. A question that names a
 * known field whose value the header lacks goes to ris in `header` mode instead.
 */

interface HeaderField {
  key: string;
  label: string;
  /** Matched on the lower-cased, accent-free question. */
  pattern: RegExp;
  /** Matched on the question as typed (case matters: "mAs" vs "más"). */
  exact?: RegExp;
  tags: Array<{ tag: string; name: string; unit?: string; date?: boolean }>;
}

export const HEADER_FIELDS: readonly HeaderField[] = [
  {
    key: 'contrast', label: 'Medio de contraste', pattern: /\bcontrast(e|ad[oa]s?)\b/,
    tags: [{ tag: '00180010', name: 'Agente' }, { tag: '00181040', name: 'Vía' }, { tag: '00181041', name: 'Volumen', unit: 'ml' }],
  },
  {
    key: 'thickness', label: 'Grosor de corte', pattern: /\b(grosor|espesor)\b|slice thickness/,
    tags: [{ tag: '00180050', name: '', unit: 'mm' }],
  },
  { key: 'kvp', label: 'kVp', pattern: /\bkvp?\b|kilovolt/, tags: [{ tag: '00180060', name: '', unit: 'kV' }] },
  {
    key: 'mas', label: 'Carga del tubo', pattern: /miliamperio|corriente (del|de) tubo/, exact: /\bmAs\b|\bmA\b/,
    tags: [{ tag: '00181152', name: 'Exposición', unit: 'mAs' }, { tag: '00181151', name: 'Corriente', unit: 'mA' }, { tag: '00181150', name: 'Tiempo', unit: 'ms' }],
  },
  { key: 'dose', label: 'Dosis', pattern: /\bdosis\b|\bctdi(vol)?\b|\bdlp\b/, tags: [{ tag: '00189345', name: 'CTDIvol', unit: 'mGy' }] },
  {
    key: 'date', label: 'Fecha', pattern: /\bfecha\b|\bcuando se (hizo|realizo|adquirio|tomo)\b/,
    tags: [{ tag: '00080022', name: 'Adquisición', date: true }, { tag: '00080020', name: 'Estudio', date: true }],
  },
  {
    key: 'equipment', label: 'Equipo', pattern: /\bequipo\b|\bfabricante\b|\bmarca\b|\bescaner\b|\bresonador\b|\btomografo\b/,
    tags: [{ tag: '00080070', name: 'Fabricante' }, { tag: '00081090', name: 'Modelo' }, { tag: '00081010', name: 'Estación' }],
  },
  {
    key: 'protocol', label: 'Protocolo', pattern: /\bprotocolo\b/,
    tags: [{ tag: '00181030', name: '' }, { tag: '0008103E', name: 'Serie' }],
  },
  {
    // "te" / "tr" only as typed in capitals: lower-case "te" is the pronoun ("¿qué te parece…?").
    key: 'mr-times', label: 'Tiempos', pattern: /tiempos? de (repeticion|eco)/, exact: /\b(TR|TE)\b/,
    tags: [{ tag: '00180080', name: 'TR', unit: 'ms' }, { tag: '00180081', name: 'TE', unit: 'ms' }],
  },
  { key: 'field', label: 'Campo magnético', pattern: /\bteslas?\b|campo magnetico/, tags: [{ tag: '00180087', name: '', unit: 'T' }] },
  {
    key: 'spacing', label: 'Tamaño de píxel', pattern: /tamano de(l)? pixel|espaciado|pixel spacing/,
    tags: [{ tag: '00280030', name: '', unit: 'mm' }],
  },
  {
    key: 'matrix', label: 'Matriz', pattern: /\bmatriz\b/,
    tags: [{ tag: '00280010', name: 'Filas' }, { tag: '00280011', name: 'Columnas' }],
  },
];

/**
 * Clinical words: "grosor de la pared", "fecha del nódulo previo", "¿hay realce con contraste?", "¿falta la
 * marca de lateralidad?"… are questions about the image, not about the header.
 */
const CLINICAL_WORDS = /\b(pared|lesion|nodulo|masa|quiste|engrosamiento|hueso|tejido|hallazgo|tumor|derrame|hemorragia|fractura|realce|refuerzo|captacion|lateralidad)\b/;

export function normalise(question: string): string {
  return question.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/** The header field a question asks about, or undefined for any other question. */
export function matchHeaderField(question: string): HeaderField | undefined {
  const plain = normalise(question);
  if (CLINICAL_WORDS.test(plain)) return undefined;
  return HEADER_FIELDS.find((f) => f.pattern.test(plain) || (f.exact?.test(question) ?? false));
}

function multiValue(json: DicomJson, tag: string): string {
  const values = json[tag]?.Value;
  if (!values || values.length <= 1) return tagString(json, tag);
  return values.map((v) => String(v)).join(' \\ ');
}

/** "Grosor de corte: 1 mm", or null when the header has none of the field's tags. */
export function answerFromHeader(field: HeaderField, json: DicomJson): string | null {
  const parts: string[] = [];
  for (const spec of field.tags) {
    const raw = multiValue(json, spec.tag).trim();
    if (!raw) continue;
    const value = spec.date ? formatDicomDate(raw) : raw;
    const withUnit = spec.unit ? `${value} ${spec.unit}` : value;
    parts.push(spec.name ? `${spec.name} ${withUnit}` : withUnit);
  }
  return parts.length > 0 ? `${field.label}: ${parts.join(' · ')}.` : null;
}

/** `matched`: the question names a header field; `answer`: its value from the image on screen, if any. */
export interface HeaderAnswer {
  matched: boolean;
  answer: string | null;
}

/** Answers a header question from the active viewport's image (metadata in memory, else one WADO-RS call). */
export async function activeHeaderAnswer(shell: ViewerShell, question: string): Promise<HeaderAnswer> {
  const field = matchHeaderField(question);
  const vp = shell.activeViewport();
  const stack = vp?.currentStack;
  const index = vp?.currentIndex();
  if (!field) return { matched: false, answer: null };
  if (!stack || index === undefined) return { matched: true, answer: null };
  const at = frameAt(stack.series, index);
  if (!at) return { matched: true, answer: null };
  try {
    const json = await fetchInstanceMetadata(shell, stack.series, at.instance);
    return { matched: true, answer: answerFromHeader(field, json) };
  } catch (error) {
    console.warn('[viewer] header metadata unavailable', error);
    return { matched: true, answer: null };
  }
}
